import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAdminAuth } from '@/services/adminAuth';
import { Logo } from '@/components/ui/Logo';

export function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading, error, clearError, checkSession } = useAdminAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && checkSession()) {
      const from = (location.state as { from?: string })?.from || '/admin/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location, checkSession]);

  // Clear error when inputs change
  useEffect(() => {
    if (error) clearError();
  }, [email, password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) return;

    const success = await login(email, password);
    if (success) {
      const from = (location.state as { from?: string })?.from || '/admin/dashboard';
      navigate(from, { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-base p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-radial from-accent/10 via-transparent to-transparent opacity-50" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-radial from-purple-500/10 via-transparent to-transparent opacity-50" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size={48} showText={false} />
          </div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">Admin Panel</h1>
          <p className="text-text-secondary">Bosphorus Labs Yonetim Sistemi</p>
        </div>

        {/* Login Card */}
        <div className="bg-bg-card border border-border-default rounded-2xl shadow-xl p-8">
          {/* Security Notice */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-6">
            <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-amber-500 font-medium">Guvenli Alan</p>
              <p className="text-amber-500/70">Bu alan sadece yetkili yoneticilere aciktir.</p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-6">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                E-posta Adresi
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@bosphoruslabs.com"
                  className={cn(
                    "w-full pl-10 pr-4 py-3 rounded-lg bg-bg-elevated border border-border-default",
                    "focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none",
                    "transition-colors placeholder:text-text-tertiary"
                  )}
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">
                Sifre
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sifrenizi girin"
                  className={cn(
                    "w-full pl-10 pr-12 py-3 rounded-lg bg-bg-elevated border border-border-default",
                    "focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none",
                    "transition-colors placeholder:text-text-tertiary"
                  )}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-text-tertiary hover:text-text-primary transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-border-default text-accent focus:ring-accent"
                />
                <span className="text-sm text-text-secondary">Beni hatirla</span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !email || !password}
              className={cn(
                "w-full py-3 rounded-lg font-semibold transition-all",
                "bg-gradient-to-r from-accent to-purple-600 text-white",
                "hover:from-accent/90 hover:to-purple-600/90",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "flex items-center justify-center gap-2"
              )}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Giris yapiliyor...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  Admin Girisi
                </>
              )}
            </button>
          </form>

          {/* Demo Credentials */}
          <div className="mt-6 pt-6 border-t border-border-default">
            <p className="text-xs text-text-tertiary text-center mb-3">Demo Giris Bilgileri:</p>
            <div className="p-2 rounded bg-bg-elevated text-xs">
              <p className="text-text-secondary">E-posta: <span className="text-text-primary font-mono">admin@bosphoruslabs.com</span></p>
              <p className="text-text-secondary">Sifre: <span className="text-text-primary font-mono">Bosphorus@2024!</span></p>
            </div>
          </div>

          {/* Footer Info */}
          <div className="mt-4 pt-4 border-t border-border-default">
            <p className="text-xs text-text-tertiary text-center">
              Guvenli kimlik dogrulama ile korunmaktadir. Tum giris denemeleri kaydedilir.
            </p>
          </div>
        </div>

        {/* Back to Site Link */}
        <div className="mt-4 text-center">
          <a href="/" className="text-sm text-text-tertiary hover:text-accent transition-colors">
            &larr; Siteye Don
          </a>
        </div>
      </div>
    </div>
  );
}
